import { useTripsListQuery } from '@bambi/api-client/trips';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { useMemo } from 'react';

import { GlobalRejectReasonMenu } from './GlobalRejectReasonMenu';
import { RejectRequestTripDetails } from './RejectRequestTripDetails';
import { useRejectTripRequestConfirmModal } from './useRejectTripRequestConfirmModal';
import { useAppSelector } from 'src/app/store/hooks';
import type { DeleteModalProps } from 'src/common/DeleteModal';
import { useDispatchModalToShow } from 'src/features/dispatch/dispatcher.slice';

export type RejectTripRequestConfirmModalProps = DeleteModalProps & {
  // The id of the single leg being rejected, when exactly one leg is selected.
  // Used to detect a wait-and-return pairing and, if found, show the W&R prompt.
  waitAndReturnLegId?: string;
};

export const useRejectTripRequestConfirmModalProps =
  (): RejectTripRequestConfirmModalProps => {
    const modalToShow = useDispatchModalToShow();
    const { error, onRejectConfirm, dismissModal } =
      useRejectTripRequestConfirmModal();

    const requestedTripIdsToRejectToReasonCodes = useAppSelector(
      (state) => state.dispatcher.modals.requestedTripIdsToRejectToReasonCodes
    );

    const requestedTripIdsToReject = useMemo(() => {
      return Object.keys(requestedTripIdsToRejectToReasonCodes);
    }, [requestedTripIdsToRejectToReasonCodes]);

    const { data: tripData, isFetching: isFetchingTrips } = useTripsListQuery(
      {
        idIn: requestedTripIdsToReject,
      },
      {
        skip: !requestedTripIdsToReject.length,
      }
    );

    const waitAndReturnLegId =
      requestedTripIdsToReject.length === 1
        ? requestedTripIdsToReject[0]
        : undefined;

    return {
      waitAndReturnLegId,
      open: modalToShow === 'rejectRequestConfirm',
      setOpen: (isOpen) => {
        if (!isOpen) {
          dismissModal();
        }
      },
      onCancel: () => dismissModal(),
      onConfirm: () => onRejectConfirm(),
      title:
        requestedTripIdsToReject.length > 1
          ? 'Reject these trips?'
          : 'Reject this trip?',
      error,
      confirmText: 'Reject',
      children: isFetchingTrips ? (
        <div className="flex justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <GlobalRejectReasonMenu />
          <RejectRequestTripDetails trips={tripData?.results || []} />
        </>
      ),
      contentClassnames: 'sm:max-w-full',
    };
  };
